import { captureMessage } from '@sentry/react';
import { FrontEggContextOptions } from '../../constants/Licenses';

export interface FronteggSubTenant {
  tenantId: string;
  tenantName?: string;
}

export interface FronteggParentTenant {
  tenantId: string;
  tenantName?: string;
  /** 0 is the direct parent; higher levels are further up the tree. */
  level: number;
}

const HIERARCHY_TIMEOUT_MS = 10_000;

function reportFailure(path: string, detail: Record<string, unknown>): void {
  captureMessage('Frontegg hierarchy lookup failed', { level: 'warning', extra: { path, ...detail } });
}

function hasTenantId(item: unknown): item is { tenantId: string } {
  return (
    typeof item === 'object' && item !== null && typeof (item as { tenantId?: unknown }).tenantId === 'string'
  );
}

function isSubTenant(item: unknown): item is FronteggSubTenant {
  return hasTenantId(item);
}

function isParentTenant(item: unknown): item is FronteggParentTenant {
  return hasTenantId(item) && typeof (item as { level?: unknown }).level === 'number';
}

// Frontegg answers these for the account that is active in the token. A
// failed, slow, or malformed lookup resolves to an empty list so callers fall
// back to the plain subdomain rule instead of blocking on hierarchy state;
// the failure is reported so a quiet fallback can still be diagnosed.
async function getHierarchy<T>(
  path: string,
  accessToken: string,
  isValid: (item: unknown) => item is T
): Promise<T[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HIERARCHY_TIMEOUT_MS);
  try {
    const response = await fetch(`${FrontEggContextOptions.baseUrl}${path}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      signal: controller.signal,
    });
    if (!response.ok) {
      reportFailure(path, { status: response.status });
      return [];
    }
    const body: unknown = await response.json();
    if (!Array.isArray(body)) {
      reportFailure(path, { reason: 'body is not a list' });
      return [];
    }
    return body.filter(isValid);
  } catch (error) {
    reportFailure(path, { error: String(error) });
    return [];
  } finally {
    clearTimeout(timer);
  }
}

/** Sub-accounts of the token's active account. */
export function getActiveTenantSubTenants(accessToken: string): Promise<FronteggSubTenant[]> {
  return getHierarchy('/tenants/resources/hierarchy/v1', accessToken, isSubTenant);
}

/** Ancestors of the token's active account. */
export function getActiveTenantParents(accessToken: string): Promise<FronteggParentTenant[]> {
  return getHierarchy('/tenants/resources/hierarchy/v1/parents', accessToken, isParentTenant);
}
